import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  TextField,
  MenuItem,
  Grid,
  Collapse,
  Button,
  Typography,
  Box,
  Chip,
} from "@mui/material";
import FilterListIcon from "@mui/icons-material/FilterList";
import SummaryGraph from "./SummaryGraph";

export default function GraphicalSummary() {
  // States
  const [showFilters, setShowFilters] = useState(true);
  const [projectId, setProjectId] = useState(null);
  const [suppliers, setSuppliers] = useState([]);
  const [supplier, setSupplier] = useState("");
  const [startDate, setStartDate] = useState("");
  const [endDate, setEndDate] = useState("");
  const [summaryData, setSummaryData] = useState([]);
  const [shifts, setShifts] = useState([]);
  const [shift, setShift] = useState("");
  const [forms, setForms] = useState([]);
  const [form, setForm] = useState("");
  const [subForms, setSubForms] = useState([]);
  const [subForm, setSubForm] = useState("");
  const [lines, setLines] = useState([]);
  const [line, setLine] = useState([]);
  const [sections, setSections] = useState([]);
  const [section, setSection] = useState("");
  const [fields, setFields] = useState([]);
  const [field, setField] = useState("");
  const baseURL = process.env.REACT_APP_API_BASE_URL;

  // Helpers
  const uniqueValues = (arr) => [...new Set(arr.filter(Boolean))];
  const getLineValue = (item) => item.line ?? item.Line ?? "";

  // Load projectId once
  useEffect(() => {
    const storedProject = localStorage.getItem("selectedProject");
    if (storedProject) setProjectId(storedProject);
  }, []);

  // Fetch suppliers
  useEffect(() => {
    if (!projectId) return;
    axios
      .post(`${baseURL}get_supplier_names`, { projectId })
      .then((res) => setSuppliers(res.data || []))
      .catch(() => setSuppliers([]));
  }, [projectId]);

  // Fetch summary data on filters
  useEffect(() => {
    if (!projectId || !supplier || !startDate || !endDate) {
      setSummaryData([]);
      setShifts([]);
      setShift("");
      setForms([]);
      setForm("");
      setSubForms([]);
      setSubForm("");
      setLines([]);
      setLine([]);
      setSections([]);
      setSection("");
      setFields([]);
      setField("");
      return;
    }
    axios
      .get(`${baseURL}graphical_summary`, {
        params: { projectId, inspectionSupplier: supplier, date: startDate, endDate },
      })
      .then((res) => setSummaryData(res.data?.data || []))
      .catch(() => setSummaryData([]));
  }, [projectId, supplier, startDate, endDate]);

  // Update shifts and forms on summaryData change
  useEffect(() => {
    if (summaryData.length === 0) {
      setShifts([]);
      setShift("");
      setForms([]);
      setForm("");
      setSubForms([]);
      setSubForm("");
      setLines([]);
      setLine([]);
      setSections([]);
      setSection("");
      setFields([]);
      setField("");
      return;
    }
    setShifts(uniqueValues(summaryData.map((d) => d.shift)));
    setShift("");
    setForms(uniqueValues(summaryData.map((d) => d.form)));
    setForm("");
    setSubForms([]);
    setSubForm("");
    setLines([]);
    setLine([]);
    setSections([]);
    setSection("");
    setFields([]);
    setField("");
  }, [summaryData]);

  // Update subForms independently filtered by form
  useEffect(() => {
    if (!form) {
      setSubForms([]);
      setSubForm("");
      return;
    }
    const filtered = summaryData.filter((d) => d.form === form);
    const subs = uniqueValues(filtered.map((d) => d.subform));
    setSubForms(subs.length === 1 && subs[0] === "" ? [] : subs);
    setSubForm("");
  }, [form, summaryData]);

  // Update lines independently filtered by form, subForm, section
  useEffect(() => {
    if (!form) {
      setLines([]);
      setLine([]);
      return;
    }
    let filtered = summaryData.filter((d) => d.form === form);
    if (subForm) filtered = filtered.filter((d) => d.subform === subForm);
    if (section) filtered = filtered.filter((d) => d.Section === section);
    const uniqueLines = uniqueValues(filtered.map(getLineValue));
    const validLines = uniqueLines.length === 1 && uniqueLines[0] === "" ? [] : uniqueLines;
    setLines(validLines);
    // Only reset line if the current selection is no longer valid
    if (line.length > 0 && !line.every((l) => validLines.includes(l))) {
      setLine([]);
    }
  }, [form, subForm, section, summaryData]);

  // Update sections independently filtered by form, subForm, line
  useEffect(() => {
    if (!form) {
      setSections([]);
      setSection("");
      return;
    }
    let filtered = summaryData.filter((d) => d.form === form);
    if (subForm) filtered = filtered.filter((d) => d.subform === subForm);
    if (line.length > 0) filtered = filtered.filter((d) => line.includes(getLineValue(d)));
    const uniqueSections = uniqueValues(filtered.map((d) => d.Section));
    const validSections = uniqueSections.length === 1 && uniqueSections[0] === "" ? [] : uniqueSections;
    setSections(validSections);
    // Only reset section if the current selection is no longer valid
    if (section && !validSections.includes(section)) {
      setSection("");
    }
  }, [form, subForm, line, summaryData]);

  // Update fields independently filtered by form, subForm, line, section
  useEffect(() => {
    if (!form) {
      setFields([]);
      setField("");
      return;
    }
    let filtered = summaryData.filter((d) => d.form === form);
    if (subForm) filtered = filtered.filter((d) => d.subform === subForm);
    if (line.length > 0) filtered = filtered.filter((d) => line.includes(getLineValue(d)));
    if (section) filtered = filtered.filter((d) => d.Section === section);
    const uniqueFields = uniqueValues(filtered.map((d) => d.field));
    const validFields = uniqueFields.length === 1 && uniqueFields[0] === "" ? [] : uniqueFields;
    setFields(validFields);
    // Only reset field if the current selection is no longer valid
    if (field && !validFields.includes(field)) {
      setField("");
    }
  }, [form, subForm, line, section, summaryData]);

  // Handlers
  const handleSupplierChange = (e) => {
    setSupplier(e.target.value);
    setShift("");
    setForm("");
    setSubForm("");
    setLine([]);
    setSection("");
    setField("");
    setSummaryData([]);
  };
  const handleShiftChange = (e) => setShift(e.target.value);
  const handleFormChange = (e) => setForm(e.target.value);
  const handleSubFormChange = (e) => setSubForm(e.target.value);
  const handleLineChange = (e) => {
    const value = e.target.value;
    setLine(typeof value === "string" ? value.split(",") : value);
  };
  const handleSectionChange = (e) => setSection(e.target.value);
  const handleFieldChange = (e) => setField(e.target.value);

  return (
    <div className="p-6 bg-gradient-to-br rounded-xl shadow-2xl w-full mx-auto">
      <Typography
        variant="h4"
        className="mb-6 font-bold text-center text-gray-800"
      >
        Graphical Summary
      </Typography>
      <div className="flex justify-between items-center mb-4">
        <Button
          startIcon={<FilterListIcon />}
          onClick={() => setShowFilters((f) => !f)}
          variant="contained"
          color="primary"
          className="shadow-md hover:scale-105 transition-transform duration-300"
          style={{ backgroundColor: "#424752", color: "#fff" }}
        >
          {showFilters ? "Hide Filters" : "Show Filters"}
        </Button>
        {field && (
          <Typography variant="body2" className="text-gray-600">
            Showing data for: <strong>{field}</strong>
            {line.length > 0 ? ` (Lines: ${line.join(", ")})` : ""}
          </Typography>
        )}
      </div>
      <Collapse in={showFilters}>
        <div className="bg-white p-4 rounded-lg shadow-md mb-6">
          <Grid container spacing={3}>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                select
                label="Supplier"
                value={supplier}
                onChange={handleSupplierChange}
                fullWidth
                size="small"
                variant="outlined"
                className="bg-gray-50"
              >
                <MenuItem value="">Select Supplier</MenuItem>
                {suppliers.map((s) => (
                  <MenuItem key={s.name} value={s.name}>
                    {s.name}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="Start Date"
                type="date"
                value={startDate}
                onChange={(e) => setStartDate(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                className="bg-gray-50"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                label="End Date"
                type="date"
                value={endDate}
                onChange={(e) => setEndDate(e.target.value)}
                fullWidth
                size="small"
                variant="outlined"
                InputLabelProps={{ shrink: true }}
                className="bg-gray-50"
              />
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                select
                label="Shift"
                value={shift}
                onChange={handleShiftChange}
                fullWidth
                size="small"
                variant="outlined"
                disabled={shifts.length === 0}
                className="bg-gray-50"
              >
                <MenuItem value="">Select Shift</MenuItem>
                {shifts.map((s) => (
                  <MenuItem key={s} value={s}>
                    {s}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            <Grid item xs={12} sm={6} md={4} lg={3}>
              <TextField
                select
                label="Form"
                value={form}
                onChange={handleFormChange}
                fullWidth
                size="small"
                variant="outlined"
                disabled={forms.length === 0}
                className="bg-gray-50"
              >
                <MenuItem value="">Select Form</MenuItem>
                {forms.map((f) => (
                  <MenuItem key={f} value={f}>
                    {f}
                  </MenuItem>
                ))}
              </TextField>
            </Grid>
            {subForms.length > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  select
                  label="Sub Form"
                  value={subForm}
                  onChange={handleSubFormChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className="bg-gray-50"
                >
                  <MenuItem value="">Select Sub Form</MenuItem>
                  {subForms.map((sf) => (
                    <MenuItem key={sf} value={sf}>
                      {sf}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {lines.length > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  select
                  label="Line"
                  value={line}
                  onChange={handleLineChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                  SelectProps={{
                    multiple: true,
                    renderValue: (selected) => (
                      <Box sx={{ display: "flex", flexWrap: "wrap", gap: 0.5 }}>
                        {selected.map((value) => (
                          <Chip key={value} label={value} size="small" />
                        ))}
                      </Box>
                    ),
                  }}
                  className="bg-gray-50"
                >
                  {lines.map((ln) => (
                    <MenuItem key={ln} value={ln}>
                      {ln}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {sections.length > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  select
                  label="Section"
                  value={section}
                  onChange={handleSectionChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className="bg-gray-50"
                >
                  <MenuItem value="">Select Section</MenuItem>
                  {sections.map((sec) => (
                    <MenuItem key={sec} value={sec}>
                      {sec}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
            {fields.length > 0 && (
              <Grid item xs={12} sm={6} md={4} lg={3}>
                <TextField
                  select
                  label="Field"
                  value={field}
                  onChange={handleFieldChange}
                  fullWidth
                  size="small"
                  variant="outlined"
                  className="bg-gray-50"
                >
                  <MenuItem value="">Select Field</MenuItem>
                  {fields.map((fd) => (
                    <MenuItem key={fd} value={fd}>
                      {fd}
                    </MenuItem>
                  ))}
                </TextField>
              </Grid>
            )}
          </Grid>
        </div>
      </Collapse>
      <SummaryGraph
        summaryData={summaryData}
        field={field}
        form={form}
        subForm={subForm}
        line={line}
        section={section}
        shift={shift}
      />
    </div>
  );
}